import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    useCallback,
} from "react";

type device = "ios" | "android" | "windows" | "macos" | "web";

interface NativeContextType {
    isNative: boolean;
    deviceType: device;
    location: any;
    postAuthToken: (accessToken: any) => void;
    postLocation: () => void;
    backIn: (key: string) => void;
    backOut: (key: string) => void;
    backed: string;
    keyboardHeight: number;
}

const NativeContext = createContext<NativeContextType | null>(null);

export const NativeProvider: React.FC<React.PropsWithChildren<{}>> = ({
    children,
}) => {
    const [isNative, setIsNative] = useState<boolean>(false);
    const [deviceType, setDeviceType] = useState<device>("web");
    const [location, setLocation] = useState(null);
    const [backStack, setBackStack] = useState<string[]>([]);
    const [backed, setBacked] = useState<string>("");
    const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

    const postMessage = useCallback((data: any) => {
        (window as any).ReactNativeWebView?.postMessage(JSON.stringify(data));
    }, []);

    useEffect(() => {
        const handleMessage = (event: any) => {
            if (event.isTrusted) return;

            try {
                const { type, data } = JSON.parse(event.data);
                switch (type) {
                    case "native":
                        console.log(type, data);
                        setIsNative(true);
                        setDeviceType(data);
                        break;
                    case "location":
                        setLocation(data);
                        break;
                    case "back":
                        backOut();
                        break;
                    case "safearea":
                        // console.log("safe-area: ", JSON.stringify(data));
                        document.documentElement.style.setProperty(
                            "--safe-area-top",
                            `${data.top}px`
                        );
                        document.documentElement.style.setProperty(
                            "--safe-area-bottom",
                            `${120}px`
                        );
                        document.documentElement.style.setProperty(
                            "--safe-area-left",
                            `${data.left}px`
                        );
                        document.documentElement.style.setProperty(
                            "--safe-area-right",
                            `${data.right}px`
                        );
                        break;

                    case "keyboardHeight":
                        setKeyboardHeight(data);
                        break;
                }
            } catch (err) {
                console.error(err);
            }
        };

        document.addEventListener("message", handleMessage);
        window.addEventListener("message", handleMessage);

        return () => {
            document.removeEventListener("message", handleMessage);
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    const postAuthToken = (accessToken: any) => {
        postMessage({
            type: "authToken",
            token: accessToken,
        });
    };

    const postLocation = () => {
        postMessage({ type: "location" });
    };

    useEffect(() => {
        postMessage({
            type: "canGoBack",
            canGoBack: backStack.length > 0,
        });
    }, [backStack]);

    const backIn = (key: string) => {
        setBackStack((prev) => [...prev, key]);
    };
    const backOut = (key?: string) => {
        setBackStack((prev) => {
            if (!key) {
                const target = prev[prev.length - 1];
                setBacked(target);
                setTimeout(() => setBacked(""), 1);
                return prev.slice(0, -1);
            } else {
                return prev.filter((item) => item !== key);
            }
        });
    };

    return (
        <NativeContext.Provider
            value={{
                isNative,
                deviceType,
                location,
                postAuthToken,
                postLocation,
                backIn,
                backOut,
                backed,
                keyboardHeight,
            }}
        >
            {children}
        </NativeContext.Provider>
    );
};

export const useNative = (): NativeContextType => {
    const context = useContext(NativeContext);
    if (!context) {
        throw new Error("useNative must be used within a NativeProvider");
    }
    return context;
};

export const useBackable = (
    id: string,
    onBack: () => void,
    appear: boolean = true
) => {
    const { backIn, backOut, backed } = useNative();

    useEffect(() => {
        if (appear) backIn(id);
        else backOut(id);
    }, [appear]);

    useEffect(() => {
        if (appear && backed === id) {
            onBack();
        }
    }, [backed]);
};
